(() => {
  if (customElements.get('junior-comparison')) return;
  // Product details stay in memory, never in storage. Only the shortlist IDs persist.
  const products = new Map();
  const shortlists = new Map();
  const el = (tag, text, className) => {
    const node = document.createElement(tag);
    if (text != null) node.textContent = text;
    if (className) node.className = className;
    return node;
  };
  const readProducts = root => {
    root.querySelectorAll('[data-compare-product]').forEach(template => {
      try {
        const p = JSON.parse(template.dataset.details);
        if (p.id && Array.isArray(p.modules) && Array.isArray(p.layouts)) products.set(p.id, p);
      } catch (_) { /* A malformed product cannot break the collection. */ }
    });
  };
  class JuniorComparison extends HTMLElement {
    connectedCallback() {
      this.events?.abort();
      this.events = new AbortController();
      const signal = this.events.signal;
      this.labels = JSON.parse(this.dataset.labels);
      this.section = this.closest('.jr-showcase');
      this.dialog = this.querySelector('dialog');
      if (!this.dialog?.showModal) return;
      this.key = `junior-compare:${this.dataset.collectionUrl}:${this.dataset.currency}`;
      try {
        const saved = JSON.parse(sessionStorage.getItem(this.key) || '[]');
        this.ids = Array.isArray(saved) ? [...new Set(saved.filter(id => typeof id === 'string' && /^\d+$/.test(id)))].slice(0, 3) : [];
      } catch (_) { this.ids = shortlists.get(this.key) || []; }
      if (shortlists.has(this.key)) this.ids = [...shortlists.get(this.key)];
      readProducts(this.section);
      this.mobile = matchMedia('(max-width:700px)');
      this.pair = this.ids.slice(0, 2);
      this.section.querySelectorAll('[data-compare-choice]').forEach(choice => { choice.hidden = false; });
      this.section.addEventListener('change', event => {
        const input = event.target.closest('[data-compare-id]');
        if (!input) return;
        if (input.checked && this.ids.length === 3) {
          input.checked = false;
          this.announce(this.labels.limit);
          this.querySelector('[data-compare-hint]').textContent = this.labels.limit;
          return;
        }
        this.ids = input.checked ? [...new Set([...this.ids, input.dataset.compareId])] : this.ids.filter(id => id !== input.dataset.compareId);
        this.save();
        this.sync();
        this.announce(this.format('selected', {count:this.ids.length}));
      }, {signal});
      this.addEventListener('click', event => {
        if (event.target.closest('[data-compare-open]')) this.open();
        if (event.target.closest('[data-compare-close]')) this.dialog.close();
        if (event.target.closest('[data-compare-retry]')) this.restore();
        if (event.target.closest('[data-compare-clear]')) {
          this.ids = [];
          this.save();
          this.sync();
          this.focusCollection();
          this.announce(this.format('selected', {count:0}));
        }
        const remove = event.target.closest('[data-compare-remove]');
        if (remove) {
          const index = this.ids.indexOf(remove.dataset.compareRemove);
          this.ids = this.ids.filter(id => id !== remove.dataset.compareRemove);
          this.save();
          this.sync();
          const buttons = this.querySelectorAll('[data-compare-remove]');
          if (buttons.length) buttons[Math.min(index, buttons.length - 1)].focus({preventScroll:true});
          else this.focusCollection();
          this.announce(this.format('selected', {count:this.ids.length}));
        }
      }, {signal});
      this.addEventListener('change', event => {
        if (!event.target.matches('[data-compare-side]')) return;
        const side = Number(event.target.dataset.compareSide);
        const old = this.pair[side];
        this.pair[side] = event.target.value;
        if (this.pair[1 - side] === event.target.value) this.pair[1 - side] = old;
        this.renderPair();
        this.renderTable();
      }, {signal});
      this.dialog.addEventListener('click', event => {
        if (event.target !== this.dialog) return;
        const r = this.dialog.getBoundingClientRect();
        if (event.clientX < r.left || event.clientX > r.right || event.clientY < r.top || event.clientY > r.bottom) this.dialog.close();
      }, {signal});
      this.dialog.addEventListener('close', () => {
        this.unlock();
        if (this.opener?.isConnected) this.opener.focus({preventScroll:true});
      }, {signal});
      this.mobile.addEventListener('change', () => { if (this.dialog.open) { this.renderPair(); this.renderTable(); } }, {signal});
      this.resize = new ResizeObserver(() => this.measure());
      this.resize.observe(this.querySelector('[data-compare-bar]'));
      this.sync();
      if (this.ids.some(id => !products.has(id))) this.restore();
    }
    disconnectedCallback() {
      this.events?.abort();
      this.request?.abort();
      this.resize?.disconnect();
      this.unlock();
      this.restoreSpacing();
    }
    format(key, values = {}) {
      if (key === 'selected' && values.count === 1) key = 'selectedOne';
      return Object.entries(values).reduce((text, [name, value]) => text.replaceAll(`[${name}]`, String(value)), this.labels[key] || '');
    }
    focusCollection() {
      const target = this.section.querySelector('[data-compare-id]') || this.section.querySelector('.jr-showcase__empty a') || this.section.querySelector('[data-junior-result-count]');
      target?.focus({preventScroll:true});
    }
    save() { shortlists.set(this.key, [...this.ids]); try { sessionStorage.setItem(this.key, JSON.stringify(this.ids)); } catch (_) { /* In-memory persistence still supports filtering. */ } }
    announce(text) { this.querySelector('[data-compare-status]').textContent = text; }
    sync() {
      this.section.querySelectorAll('[data-compare-id]').forEach(input => { input.checked = this.ids.includes(input.dataset.compareId); });
      this.querySelector('[data-compare-bar]').hidden = !this.ids.length;
      this.querySelector('[data-compare-count]').textContent = this.format('selected', {count:this.ids.length});
      const list = this.querySelector('[data-compare-selected]');
      list.replaceChildren();
      this.ids.forEach(id => {
        const p = products.get(id);
        if (!p) return;
        const item = el('li');
        item.append(el('span', p.title));
        const button = el('button', '×', 'jr-compare-remove');
        button.type = 'button';
        button.dataset.compareRemove = id;
        button.setAttribute('aria-label', this.format('remove', {product:p.title}));
        item.append(button);
        list.append(item);
      });
      this.querySelector('[data-compare-open]').disabled = this.ids.length < 2 || this.ids.some(id => !products.has(id)) || this.loading;
      this.querySelector('[data-compare-hint]').textContent = this.loading ? this.labels.loading : this.failed ? this.labels.error : this.ids.length === 1 ? this.labels.choose : '';
      this.querySelector('[data-compare-retry]').hidden = !this.failed;
      this.measure();
    }
    measure() {
      const bar = this.querySelector('[data-compare-bar]');
      if (bar.hidden) { this.restoreSpacing(); return; }
      if (!this.spacing) {
        this.spacing = {padding:document.body.style.paddingBottom, scroll:document.documentElement.style.scrollPaddingBottom};
        this.basePadding = parseFloat(getComputedStyle(document.body).paddingBottom) || 0;
      }
      const size = bar.getBoundingClientRect().height + 32;
      document.body.style.paddingBottom = `${this.basePadding + size}px`;
      document.documentElement.style.scrollPaddingBottom = `${size}px`;
    }
    restoreSpacing() {
      if (!this.spacing) return;
      document.body.style.paddingBottom = this.spacing.padding;
      document.documentElement.style.scrollPaddingBottom = this.spacing.scroll;
      this.spacing = null;
    }
    async restore() {
      this.request?.abort();
      this.request = new AbortController();
      this.loading = true;
      this.failed = false;
      this.sync();
      try {
        // A filtered page may omit shortlisted products. Fetch the unfiltered collection,
        // retaining preview/market context but not filters, sorting or pagination.
        const url = new URL(location.href);
        url.pathname = this.dataset.collectionUrl;
        for (const key of [...url.searchParams.keys()]) {
          if (key.startsWith('filter.') || ['page','sort_by','section_id','sections'].includes(key)) url.searchParams.delete(key);
        }
        url.searchParams.set('view', 'growplay-junior-collection');
        url.hash = '';
        const response = await fetch(url, {signal:this.request.signal});
        if (!response.ok) throw new Error('Collection unavailable');
        const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
        const component = doc.querySelector('junior-comparison');
        if (!component) throw new Error('Comparison unavailable');
        readProducts(doc);
        // Do not remove unseen products: they may be on another collection page.
        if (this.ids.some(id => !products.has(id))) throw new Error('Shortlisted product unavailable');
      } catch (error) {
        if (error.name === 'AbortError') return;
        this.failed = true;
      } finally {
        this.loading = false;
        if (this.isConnected) this.sync();
      }
    }
    open() {
      if (this.ids.length < 2 || this.ids.some(id => !products.has(id))) return;
      this.opener = document.activeElement;
      this.pair = this.ids.slice(0, 2);
      this.renderPair();
      this.renderTable();
      this.dialog.showModal();
      this.overflow = document.documentElement.style.overflow;
      document.documentElement.style.overflow = 'hidden';
    }
    unlock() {
      if (this.overflow === undefined) return;
      document.documentElement.style.overflow = this.overflow;
      this.overflow = undefined;
    }
    renderPair() {
      this.querySelector('[data-compare-pair]').hidden = !this.mobile.matches || this.ids.length < 3;
      this.querySelectorAll('[data-compare-side]').forEach((select, index) => {
        if ([...select.options].map(option => option.value).join() !== this.ids.join()) {
          select.replaceChildren(...this.ids.map(id => {
            const option = el('option', products.get(id).title);
            option.value = id;
            return option;
          }));
        }
        select.value = this.pair[index];
      });
    }
    renderTable() {
      const ids = this.mobile.matches ? this.pair : this.ids;
      const items = ids.map(id => products.get(id));
      const table = el('table', null, 'jr-compare-table');
      const head = table.createTHead().insertRow();
      const empty = el('td');
      empty.setAttribute('aria-hidden','true');
      head.append(empty);
      items.forEach(p => {
        const cell = el('th');
        cell.scope = 'col';
        if (p.image) {
          const image = el('img');
          image.src = p.image;
          image.alt = '';
          image.width = 240;
          image.height = 180;
          cell.append(image);
        } else cell.append(el('div', this.labels.pending, 'jr-compare-image'));
        cell.append(el('h3', p.title));
        head.append(cell);
      });
      const body = table.createTBody();
      const row = (label, values, render, sublabel) => {
        const tr = body.insertRow();
        if (new Set(values.map(v => JSON.stringify(v))).size > 1) tr.className = 'jr-compare-different';
        const th = el('th', label);
        th.scope = 'row';
        if (sublabel) th.append(el('small', sublabel));
        tr.append(th);
        values.forEach((value, index) => { const td = tr.insertCell(); render(td, value, items[index]); });
      };
      row(this.labels.price, items.map(p => p.price), (td, price) => td.append(el('span', price || this.labels.details, 'jr-compare-price')));
      row(this.labels.modules, items.map(p => p.count), (td, value) => { td.textContent = value ?? '—'; });
      const modules = new Map();
      items.forEach(p => p.modules.forEach(m => modules.set(m.code, m)));
      ['S','K','P','D','T'].filter(code => modules.has(code)).forEach(code => {
        const module = modules.get(code);
        row(module.type, items.map(p => p.modules.some(m => m.code === code)), (td, included) => {
          const status = el('span', '', included ? 'jr-compare-present' : 'jr-compare-absent');
          const icon = el('span', included ? '✓' : '−', 'jr-compare-status-icon');
          icon.setAttribute('aria-hidden', 'true');
          status.append(icon, el('span', included ? this.labels.included : this.labels.notIncluded));
          td.append(status);
        }, module.name);
      });
      row(this.labels.layouts, items.map(p => p.layouts), (td, layouts, p) => {
        if (!layouts.length) { const a = el('a', this.labels.details); a.href = p.url; td.append(a); return; }
        const list = el('ul');
        layouts.forEach(layout => {
          const li = el('li');
          li.append(el('strong', layout.name));
          if (Number(layout.length) > 0 && Number(layout.width) > 0) li.append(el('span', `${(Number(layout.length) / 1000).toFixed(1)} × ${(Number(layout.width) / 1000).toFixed(1)} m`));
          list.append(li);
        });
        td.append(list);
      });
      const foot = table.createTFoot().insertRow();
      const blank = foot.insertCell();
      blank.setAttribute('aria-hidden','true');
      items.forEach(p => { const cell = foot.insertCell(); const a = el('a', this.format('view', {product:p.title}), 'jr-compare-button'); a.href = p.url; cell.append(a); });
      this.querySelector('[data-compare-table]').replaceChildren(table);
    }
  }
  customElements.define('junior-comparison', JuniorComparison);
})();
